import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { supabase } from "../supabaseClient";
import Header from "../components/Header";

const STATUS_BADGE = {
  pending:  "bg-[#ffd978]/15 text-[#ffd978] border-[#ffd978]/30",
  accepted: "bg-green-500/15 text-green-400 border-green-500/30",
  denied:   "bg-red-500/15 text-red-400 border-red-500/30",
};

export default function AppealsDepth() {
  const { id } = useParams();

  const [appeal, setAppeal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState("");

  useEffect(() => {
    loadAppeal();
  }, [id]);

  async function loadAppeal() {
    setLoading(true);
    const { data } = await supabase
      .from("ban_appeals")
      .select("*")
      .eq("id", id)
      .single();

    if (data) {
      setAppeal(data);
      setNote(data.staff_note || "");
    }
    setLoading(false);
  }

  async function decide(decision) {
    if (!note.trim()) {
      alert("Please add a staff note before making a decision.");
      return;
    }
    setSaving(true);

    try {
      // Update Supabase
      const { error } = await supabase
        .from("ban_appeals")
        .update({ status: decision, staff_note: note.trim() })
        .eq("id", id);
      if (error) throw error;

      // Update the Discord embed
      if (appeal.discord_message_id) {
        await fetch("/api/send-appeal-embed", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            action: "update",
            secret: process.env.REACT_APP_DM_WEBHOOK_SECRET,
            message_id: appeal.discord_message_id,
            discord_id: appeal.discord_id,
            discord_username: appeal.discord_username,
            roblox_username: appeal.roblox_username,
            ban_reason: appeal.ban_reason,
            appeal_text: appeal.appeal_text,
            evidence: appeal.evidence,
            status: decision,
            staff_note: note.trim(),
          }),
        });
      }

      // DM the user
      if (appeal.discord_id) {
        await fetch("/api/send-discord-dm", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            discord_id: appeal.discord_id,
            discord_username: appeal.discord_username,
            status: decision === "accepted" ? "appeal_accepted" : "appeal_denied",
            staff_note: note.trim(),
            secret: process.env.REACT_APP_DM_WEBHOOK_SECRET,
          }),
        });
      }

      setAppeal(prev => ({ ...prev, status: decision, staff_note: note.trim() }));
      alert(`Appeal ${decision}. User has been DM'd.`);
    } catch (err) {
      console.error(err);
      alert("Failed to save decision. Check console.");
    }

    setSaving(false);
  }

  if (loading) {
    return (
      <main className="min-h-screen bg-[#050505] text-white">
        <Header />
        <section className="px-6 pt-36 md:px-[7%]">
          <p className="text-xl font-black">Loading appeal...</p>
        </section>
      </main>
    );
  }

  if (!appeal) {
    return (
      <main className="min-h-screen bg-[#050505] text-white">
        <Header />
        <section className="px-6 pt-36 md:px-[7%]">
          <h1 className="outfall-heading text-7xl text-[#ffd978]">Appeal Not Found</h1>
          <Link to="/appeals-review" className="outfall-button mt-6">Back To Appeals</Link>
        </section>
      </main>
    );
  }

  const decided = appeal.status !== "pending";

  return (
    <main className="min-h-screen bg-[#050505] text-white">
      <Header />

      <section className="px-6 pt-36 pb-24 md:px-[7%]">
        <Link to="/appeals-review" className="font-black uppercase text-[#ffb56f] hover:text-[#ffd978]">
          ← Back To Appeals
        </Link>

        <p className="mt-8 font-black uppercase text-[#ffb56f]">Appeal Review</p>
        <div className="flex flex-wrap items-end gap-4">
          <h1 className="outfall-heading text-[clamp(48px,7vw,100px)] leading-none text-[#ffd978] drop-shadow-[0_0_18px_#ff8f6b]">
            {appeal.roblox_username}
          </h1>
          <span className={`mb-2 rounded-full border px-4 py-2 font-black uppercase text-sm ${STATUS_BADGE[appeal.status]}`}>
            {appeal.status}
          </span>
        </div>

        {/* Info cards */}
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="rounded-2xl border border-[#ffb56f]/30 bg-black/50 p-5">
            <p className="font-black uppercase text-[#ffb56f] text-sm">Discord</p>
            <p className="mt-2 font-black text-white">{appeal.discord_username || "—"}</p>
            <p className="text-xs font-bold text-white/30 font-mono">{appeal.discord_id}</p>
          </div>
          <div className="rounded-2xl border border-[#ffb56f]/30 bg-black/50 p-5">
            <p className="font-black uppercase text-[#ffb56f] text-sm">Roblox</p>
            <p className="mt-2 font-black text-white">{appeal.roblox_username}</p>
          </div>
          <div className="rounded-2xl border border-[#ffb56f]/30 bg-black/50 p-5">
            <p className="font-black uppercase text-[#ffb56f] text-sm">Submitted</p>
            <p className="mt-2 font-black text-white">{new Date(appeal.created_at).toLocaleString()}</p>
          </div>
          <div className="rounded-2xl border border-[#ffb56f]/30 bg-black/50 p-5">
            <p className="font-black uppercase text-[#ffb56f] text-sm">Evidence</p>
            {appeal.evidence ? (
              <a href={appeal.evidence} target="_blank" rel="noopener noreferrer"
                className="mt-2 block font-black text-[#ffd978] underline truncate">
                View Link
              </a>
            ) : (
              <p className="mt-2 font-black text-white/30">None provided</p>
            )}
          </div>
        </div>

        {/* Appeal content */}
        <div className="mt-8 rounded-[28px] border-4 border-[#ffd978] bg-black/60 p-6 shadow-[0_0_35px_rgba(255,143,107,0.35)] md:p-10 grid gap-6">
          <div>
            <p className="mb-2 font-black uppercase text-[#ffb56f]">Why they were banned</p>
            <p className="rounded-xl bg-black/40 p-4 font-bold text-white leading-relaxed select-text cursor-text">{appeal.ban_reason}</p>
          </div>
          <div>
            <p className="mb-2 font-black uppercase text-[#ffb56f]">Their appeal</p>
            <p className="rounded-xl bg-black/40 p-4 font-bold text-white leading-relaxed select-text cursor-text">{appeal.appeal_text}</p>
          </div>

          {/* Staff note + decision */}
          <div className="border-t border-[#ffd978]/20 pt-6">
            <p className="mb-2 font-black uppercase text-[#ffb56f]">
              Staff Note {!decided && <span className="text-[#ffd978]">*</span>}
            </p>
            {decided ? (
              <p className="rounded-xl bg-black/40 p-4 font-bold text-white leading-relaxed select-text cursor-text">
                {appeal.staff_note || "—"}
              </p>
            ) : (
              <textarea
                className="w-full rounded-xl border border-[#ffb56f]/30 bg-black/50 px-4 py-3 font-bold text-white outline-none placeholder:text-white/40 focus:border-[#ffd978] min-h-[100px] resize-none"
                placeholder="Required — this note will be sent to the user in their DM"
                value={note}
                onChange={e => setNote(e.target.value)}
              />
            )}
          </div>

          {/* Action buttons */}
          {decided ? (
            <div className={`rounded-xl border px-5 py-4 font-black text-lg ${STATUS_BADGE[appeal.status]}`}>
              {appeal.status === "accepted"
                ? "✅ This appeal was accepted — the user has been notified."
                : "❌ This appeal was denied — the user has been notified."}
            </div>
          ) : (
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => decide("accepted")}
                disabled={saving}
                className="rounded-xl bg-green-500 px-6 py-3 font-black text-black transition hover:bg-green-400 disabled:opacity-50"
              >
                {saving ? "Saving…" : "✅ Accept Appeal"}
              </button>
              <button
                onClick={() => decide("denied")}
                disabled={saving}
                className="rounded-xl bg-red-500 px-6 py-3 font-black text-white transition hover:bg-red-400 disabled:opacity-50"
              >
                {saving ? "Saving…" : "❌ Deny Appeal"}
              </button>
            </div>
          )}
        </div>
      </section>
    </main>
  );
}
